/**
 * useChatLiveListener — produces the three live-listener handlers
 * (`added` / `modified` / `removed`) for the `useChat` orchestration hook.
 *
 * Extracted from `useChat.ts` to keep the main hook below the size threshold
 * (`presentation.md` — >200 lines is Should Fix; this trio plus the bottom-
 * scroll inference accounts for ~150 lines of the original file).
 *
 * The handlers are bound exactly ONCE in the consumer (`subscribeToChatUpdatesUseCase.execute(...)`)
 * so we cannot close over state — they read from refs (`isAtBottomRef`,
 * `chatUsersInfo`) and write via setters passed in as arguments.
 *
 * Flutter source of truth: `chat_screen.dart:497-621` (the three
 * `DocumentChangeType` branches).
 */
import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';

import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { MarkPeerMessageSeenUseCase } from '@/domain/usecases/chat/markPeerMessageSeenUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

export interface UseChatLiveListenerArgs {
  chatUsersInfo: ChatUsersInfoEntity;
  /**
   * Ref tracking whether the user is currently pinned to the bottom of the
   * (reversed) list. The peer-message `added` branch routes to the visible
   * list (+ mark seen) when true, or to the unread bucket when false.
   */
  isAtBottomRef: MutableRefObject<boolean>;
  /**
   * Ref tracking whether the current user's browser tab is visible. Web mirror
   * of Flutter `_appLifecycleState` (visible ⇔ `index == 0` / resumed, hidden
   * ⇔ `index == 2` / paused, `chat_screen.dart:131-153`). The peer-message
   * `added` branch checks this FIRST (mirroring Flutter's background-first
   * branch order at `chat_screen.dart:543-579`): when the tab is hidden the
   * incoming peer message is routed to the unread bucket — never inserted,
   * never marked seen — REGARDLESS of `isAtBottomRef`, so a backgrounded
   * reader does not send a false read-receipt to the sender.
   */
  isTabVisibleRef: MutableRefObject<boolean>;
  markPeerMessageSeenUseCase: MarkPeerMessageSeenUseCase;
  setMessages: Dispatch<SetStateAction<ChatMessageEntity[]>>;
  setOpenTimestampForMessageId: Dispatch<
    SetStateAction<Record<number, boolean>>
  >;
  setUnreadItems: Dispatch<SetStateAction<ChatMessageEntity[]>>;
  /**
   * Setter for the transient unread-pill visibility flag — flipped to `true`
   * on every peer-message-while-scrolled-away arrival, and back to `false`
   * by a 3 s `setTimeout` scheduled here. Mirrors Flutter's
   * `_unreadMessageReceived` (`chat_screen.dart:566-578`) so the pill renders
   * the most-recent unread for 3 s then degrades to the scroll-down button;
   * subsequent peer arrivals restart the cycle (each arrival is its own pill
   * -> 3 s -> button transition).
   */
  setUnreadBannerVisible: Dispatch<SetStateAction<boolean>>;
  /**
   * Ref holding the handle of the pending 3 s auto-hide `setTimeout`, so the
   * listener can clear and reschedule on consecutive peer arrivals (and the
   * page dispose path can clear it on unmount).
   */
  unreadBannerTimerRef: MutableRefObject<number | null>;
}

export interface UseChatLiveListenerResult {
  handleAdded: (incoming: ChatMessageEntity) => void;
  handleModified: (incoming: ChatMessageEntity) => void;
  handleRemoved: (incoming: ChatMessageEntity) => void;
}

export const useChatLiveListener = (
  args: UseChatLiveListenerArgs,
): UseChatLiveListenerResult => {
  const {
    chatUsersInfo,
    isAtBottomRef,
    isTabVisibleRef,
    markPeerMessageSeenUseCase,
    setMessages,
    setOpenTimestampForMessageId,
    setUnreadItems,
    setUnreadBannerVisible,
    unreadBannerTimerRef,
  } = args;

  /**
   * `added` branch (`chat_screen.dart:507-580`).
   *
   * Flutter's "current user message inserted twice" guard is a documented
   * dedupe — an optimistic insert lands in the list before the Firestore
   * write echoes back as an `added` change with the same `documentId`. The
   * Flutter side detects this by walking the list and replacing the
   * existing entry in-place; we mirror that exactly (functional setState +
   * splice replacement) so the optimistic and authoritative copies are
   * reconciled cleanly. Mirroring Flutter's peer-`added` branch ORDER at
   * `chat_screen.dart:543-579` (background-first, then scroll):
   *  - own message → always insert + (presentation layer scrolls to bottom).
   *  - peer message while the tab is HIDDEN (web: `!isTabVisibleRef.current`,
   *    Flutter `_appLifecycleState.index == 2` / app backgrounded — the FIRST
   *    branch at `chat_screen.dart:543-546`) → push to the unread bucket and
   *    surface the 3 s pill. NO insert, NO scroll, NO mark-seen — REGARDLESS
   *    of `isAtBottomRef`. This stops a backgrounded reader from sending a
   *    false read-receipt to the sender while not actually looking at the chat.
   *  - peer message while the tab is VISIBLE and at bottom (web:
   *    `isAtBottomRef.current && isTabVisibleRef.current`) → insert AND mark
   *    seen. Note: Flutter has TWO thresholds (`<= 100` for the insert, `<= 0`
   *    for the mark-seen) because the Flutter scroll controller's
   *    `position.pixels` is a continuous value and the gap accommodates
   *    fractional scroll offsets. The web `isAtBottomRef` is a boolean derived
   *    by the consuming list component; we collapse the two thresholds into the
   *    single boolean — see deviation note in the plan file.
   *  - peer message while the tab is VISIBLE but NOT at bottom → push to
   *    unread bucket.
   *
   * The tab-visibility gate is collapsed into a single
   * `treatAsAtBottom = isAtBottomRef.current && isTabVisibleRef.current`
   * boolean used wherever the peer path previously read the bare
   * `isAtBottomRef.current` — folding Flutter's "background first, then scroll"
   * ordering into the web's existing boolean structure without touching the
   * own-message or documentId-dedupe branches.
   */
  const handleAdded = useCallback(
    (incoming: ChatMessageEntity) => {
      // Collapse Flutter's "background first, then scroll" ordering
      // (`chat_screen.dart:543-579`) into one boolean: a peer message is only
      // treated as "at bottom" (insert + mark seen) when the user is pinned to
      // the bottom AND the tab is visible. A hidden tab (Flutter
      // `_appLifecycleState.index == 2`) forces this `false` REGARDLESS of
      // scroll position, so the peer path routes the message to the unread
      // bucket instead — no insert, no scroll, no mark-seen.
      const treatAsAtBottom =
        isAtBottomRef.current && isTabVisibleRef.current;

      setMessages((prev) => {
        // Dedupe against an optimistic copy still in the list
        // (`chat_screen.dart:508-524`).
        const existingIdx = prev.findIndex(
          (m) => m.documentId === incoming.documentId,
        );
        if (existingIdx !== -1) {
          const existing = prev[existingIdx];

          // Locally-failed media guard (Finding 3): a media send writes its
          // Firestore doc REGARDLESS of upload success
          // (`SendMediaChatMessageUseCase` step 4 / `medias_repository.dart:252`),
          // so even a FAILED upload echoes an `added` doc carrying
          // `isUploading: true, success: true`. `useChat.runMediaSendForId`
          // has already flipped the optimistic tile to
          // `success: false, isUploading: false` for the failure affordance;
          // overwriting it here would clobber that flip and leave a permanent
          // "Processing" tile (the failed upload never transcodes, so no
          // `modified` ever clears `isUploading`). When the local entry is
          // already flagged failed AND the echo is still `isUploading`,
          // preserve the local failed state. A successful retry flips the
          // local entry back to `success: true` first, so this guard does not
          // block legitimate reconciliation. (Text sends never hit this: the
          // text use case does NOT write on failure.)
          if (existing.success === false && incoming.isUploading === true) {
            return prev;
          }

          const next = prev.slice();
          next[existingIdx] = incoming;
          next.sort(ChatUtils.sortByTimestampDesc);
          return next;
        }

        const fromCurrentUser = incoming.idFrom === chatUsersInfo.currentFirebaseId;
        if (fromCurrentUser) {
          // Insert + sort. Scroll-to-bottom is the page's concern
          // (`chat_screen.dart:531-534`).
          const next = [incoming, ...prev];
          next.sort(ChatUtils.sortByTimestampDesc);
          return next;
        }

        if (treatAsAtBottom) {
          // Peer message + tab visible + user at bottom: include in main list
          // + mark seen below. The mark-seen call is fire-and-forget (see after
          // setMessages).
          const next = [incoming, ...prev];
          next.sort(ChatUtils.sortByTimestampDesc);
          return next;
        }

        // Peer message + (tab hidden OR user scrolled away): defer to unread
        // bucket (`chat_screen.dart:543-578`). Done via the separate
        // `setUnreadItems` call below so this returner stays pure for the
        // `prev` list.
        return prev;
      });

      // Always reserve a slot in the timestamp-open map for the incoming
      // message's timestamp, defaulting to `false` (closed) — Flutter
      // `_messageTimeSeen[chatMessage.timestamp] = false`
      // (`chat_screen.dart:541, 663`).
      setOpenTimestampForMessageId((prev) => {
        if (prev[incoming.timestamp] === false) return prev;
        return { ...prev, [incoming.timestamp]: false };
      });

      // Side effects that depend on the runtime branch but didn't fit in
      // the pure setMessages updater.
      const fromCurrentUser = incoming.idFrom === chatUsersInfo.currentFirebaseId;
      if (!fromCurrentUser) {
        if (treatAsAtBottom) {
          // Mark seen (peer message, tab visible, user at bottom).
          if (chatUsersInfo.groupChatId) {
            void markPeerMessageSeenUseCase
              .execute(chatUsersInfo.groupChatId, incoming)
              .catch((error: unknown) => {
                appLogger.error(
                  'markPeerMessageSeen (live-added) failed',
                  'useChatLiveListener.handleAdded',
                  { error: error as Error },
                );
              });
          }
        } else {
          // Push to unread bucket — Flutter `_unreadListItems.add(...)`
          // (`chat_screen.dart:567`). The bucket MUST be appended even while
          // the tab is hidden so the message is tracked and the resume branch
          // (`useChat.raiseUnreadPill`, Flutter `chat_screen.dart:141-152`) can
          // re-show the preview when the user returns.
          setUnreadItems((prev) => [...prev, incoming]);

          // Flutter parity (`chat_screen.dart:566-578`):
          // `_unreadMessageReceived = true; setState();` then
          // `Future.delayed(3s, () { _unreadMessageReceived = false; setState(); })`.
          // Each new peer arrival restarts the 3 s window so the pill always
          // previews the MOST RECENT unread message before degrading back to
          // `ScrolledUpButton`. The page derives the preview text from the
          // newest item in `unreadItems`.
          //
          // Skip the pill visibility + timer while the tab is HIDDEN: that
          // work is invisible (the user is not looking) and a background
          // `setTimeout` would expire the pill before they return. Flutter
          // shows the pill only on resume in this case (`chat_screen.dart:141-
          // 152`), which `useChat` re-arms via its `onVisibilityChange`
          // resume branch. The unread bucket above is still appended so the
          // resume branch has something to preview.
          if (isTabVisibleRef.current) {
            setUnreadBannerVisible(true);
            if (unreadBannerTimerRef.current !== null) {
              window.clearTimeout(unreadBannerTimerRef.current);
            }
            unreadBannerTimerRef.current = window.setTimeout(() => {
              setUnreadBannerVisible(false);
              unreadBannerTimerRef.current = null;
            }, ANIMATION_CONSTANTS.duration.autoHideDelay);
          }
        }
      }
    },
    [
      chatUsersInfo.currentFirebaseId,
      chatUsersInfo.groupChatId,
      isAtBottomRef,
      isTabVisibleRef,
      markPeerMessageSeenUseCase,
      setMessages,
      setOpenTimestampForMessageId,
      setUnreadItems,
      setUnreadBannerVisible,
      unreadBannerTimerRef,
    ],
  );

  /**
   * `modified` branch (`chat_screen.dart:582-607`).
   *
   * Replace the existing entry in-place (lookup by `documentId`) and re-sort.
   * No-op when the doc isn't in the local list (e.g. a `modified` for an
   * older page that hasn't been fetched yet).
   */
  const handleModified = useCallback(
    (incoming: ChatMessageEntity) => {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.documentId === incoming.documentId);
        if (idx === -1) return prev;
        const next = prev.slice();
        next[idx] = incoming;
        next.sort(ChatUtils.sortByTimestampDesc);
        return next;
      });
    },
    [setMessages],
  );

  /**
   * `removed` branch (`chat_screen.dart:608-620`).
   *
   * Splice the matching entry. No-op when absent.
   */
  const handleRemoved = useCallback(
    (incoming: ChatMessageEntity) => {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.documentId === incoming.documentId);
        if (idx === -1) return prev;
        const next = prev.slice();
        next.splice(idx, 1);
        // No need to re-sort after a delete — the remaining order is unchanged.
        return next;
      });
    },
    [setMessages],
  );

  return { handleAdded, handleModified, handleRemoved };
};
